import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Chip,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

interface RequestRow {
	requestID: number;
	supplierName: string;
	supplierEmail: string;
	year: number;
	status: string;
	requestToken: string;
	emailSentAt: string | null;
	openedAt: string | null;
	signedUpAt: string | null;
	respondedAt: string | null;
	responseTonnes: number | null;
	responseName: string | null;
	responseNotes: string | null;
	createdAt: string;
}

const STATUS_COLOR: Record<string, "default" | "secondary" | "success"> = {
	created: "default",
	sent: "default",
	opened: "secondary",
	signed_up: "success",
	responded: "success",
};

/** Supplier data requests (P4): request emissions data, track conversions. */
const SupplierRequests: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [rows, setRows] = useState<RequestRow[]>([]);
	const [msg, setMsg] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [form, setForm] = useState({
		supplierName: "",
		supplierEmail: "",
		year: String(new Date().getFullYear() - 1),
		message: "",
	});

	const call = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});
			const data = await res.json().catch(() => ({}));
			return { ok: res.ok, data: data.Data || data };
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		const r = await call("/api/supplier-requests");
		if (r.ok) setRows(r.data.requests || []);
	}, [call]);

	useEffect(() => {
		load();
	}, [load]);

	const create = async () => {
		setBusy(true);
		setMsg(null);
		try {
			const r = await call("/api/supplier-requests", {
				method: "POST",
				body: JSON.stringify(form),
			});
			setMsg(r.data.Message || r.data.message || null);
			if (r.ok) {
				setForm({
					...form,
					supplierName: "",
					supplierEmail: "",
					message: "",
				});
				await load();
			}
		} finally {
			setBusy(false);
		}
	};

	const copyLink = (token: string) => {
		const url = `${window.location.origin}/supplier-request?token=${token}`;
		navigator.clipboard?.writeText(url);
		setMsg("Link copied to clipboard");
	};

	return (
		<Box maxWidth={1000} margin="0 auto" padding={3}>
			<Typography variant="h5" gutterBottom>
				Supplier data requests
			</Typography>
			<Typography variant="body2" color="text.secondary" gutterBottom>
				Ask your suppliers for their emissions data. Each request sends
				a personalised email (or a link you can share) that guides the
				supplier to provide their data through ARTEH — and you can see
				who has opened it and who has signed up.
			</Typography>

			{msg && (
				<Alert
					severity="info"
					sx={{ my: 2 }}
					onClose={() => setMsg(null)}
				>
					{msg}
				</Alert>
			)}

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				<Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
					<TextField
						label="Supplier name"
						size="small"
						value={form.supplierName}
						onChange={(e) =>
							setForm({ ...form, supplierName: e.target.value })
						}
						sx={{ minWidth: 200 }}
					/>
					<TextField
						label="Supplier email"
						size="small"
						value={form.supplierEmail}
						onChange={(e) =>
							setForm({ ...form, supplierEmail: e.target.value })
						}
						sx={{ minWidth: 220 }}
					/>
					<TextField
						label="Year"
						size="small"
						value={form.year}
						onChange={(e) =>
							setForm({ ...form, year: e.target.value })
						}
						sx={{ width: 100 }}
					/>
					<TextField
						label="Message (optional)"
						size="small"
						value={form.message}
						onChange={(e) =>
							setForm({ ...form, message: e.target.value })
						}
						sx={{ minWidth: 220, flexGrow: 1 }}
					/>
					<Button
						variant="contained"
						color="secondary"
						disabled={
							busy ||
							form.supplierName.trim().length < 2 ||
							!form.supplierEmail.includes("@")
						}
						onClick={create}
					>
						{busy ? "Sending…" : "Send request"}
					</Button>
				</Box>
			</Paper>

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				{rows.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No requests yet. Send your first one above.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Supplier</TableCell>
								<TableCell>Email</TableCell>
								<TableCell>Year</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Link</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((r) => (
								<TableRow key={r.requestID}>
									<TableCell>{r.supplierName}</TableCell>
									<TableCell>{r.supplierEmail}</TableCell>
									<TableCell>{r.year}</TableCell>
									<TableCell>
										<Chip
											size="small"
											label={r.status.replace("_", " ")}
											color={
												STATUS_COLOR[r.status] ||
												"default"
											}
										/>
										{r.responseTonnes != null && (
											<Tooltip
												title={
													r.responseNotes ||
													"No note provided"
												}
											>
												<Typography
													variant="caption"
													display="block"
													sx={{ mt: 0.5 }}
												>
													{Number(
														r.responseTonnes,
													).toLocaleString()}{" "}
													t CO₂e
													{r.responseName
														? ` · ${r.responseName}`
														: ""}
												</Typography>
											</Tooltip>
										)}
									</TableCell>
									<TableCell>
										<Tooltip title="Copy supplier link">
											<IconButton
												size="small"
												aria-label="Copy link"
												onClick={() =>
													copyLink(r.requestToken)
												}
											>
												<ContentCopyIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>
		</Box>
	);
};

export default SupplierRequests;
